import { useEffect, useRef } from "react";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import axiosInstance from "../config/axios";
import {
  clearAllAuthSession,
  getLoginAt,
  getSessionTimeoutMs,
  setAppSession,
  setFirebaseLoginAt,
} from "../utils/appAuth";

/**
 * Absolute wall-clock logout from login time (even if the user never clicks Sign Out):
 * agent/broker → 30 minutes; admin/superuser → 3 hours.
 */
export default function useAbsoluteSessionTimeout({
  role,
  isAuthenticated,
  firebaseUser,
  appSession,
  setUser,
  setAppSessionState,
}) {
  const loggingOutRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || !role) return undefined;

    let loginAt = getLoginAt(firebaseUser, appSession);
    if (!loginAt) {
      loginAt = Date.now();
      if (appSession?.token) {
        const next = { ...appSession, loginAt };
        setAppSession(next);
        setAppSessionState?.(next);
      } else if (firebaseUser) {
        setFirebaseLoginAt(loginAt);
      }
    }

    const timeoutMs = getSessionTimeoutMs(role);
    const expiresAt = loginAt + timeoutMs;

    const forceLogout = async () => {
      if (loggingOutRef.current) return;
      loggingOutRef.current = true;
      try {
        const entryId = sessionStorage.getItem("accessLogEntryId");
        if (entryId) {
          try {
            await axiosInstance.patch(`/access-log/${entryId}/leave`);
          } catch (_) {}
          sessionStorage.removeItem("accessLogEntryId");
        }
        await signOut(auth).catch(() => {});
        clearAllAuthSession();
        setUser?.(null);
        setAppSessionState?.(null);
        window.location.href = "/";
      } catch (_) {
        clearAllAuthSession();
        window.location.href = "/";
      }
    };

    const checkExpired = () => {
      if (Date.now() >= expiresAt) {
        forceLogout();
        return true;
      }
      return false;
    };

    if (checkExpired()) return undefined;

    // Primary timer + interval backup (browsers throttle lone setTimeout in background tabs).
    const remaining = Math.max(expiresAt - Date.now(), 0);
    const timer = window.setTimeout(forceLogout, remaining);
    const interval = window.setInterval(checkExpired, 15 * 1000);
    const onVisible = () => {
      if (document.visibilityState === "visible") checkExpired();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);

    return () => {
      window.clearTimeout(timer);
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [
    role,
    isAuthenticated,
    firebaseUser,
    appSession,
    setUser,
    setAppSessionState,
  ]);
}
